import React, { useState, useMemo, useEffect } from 'react';
import { Table, Button, Popconfirm, message, Input, Icon, Checkbox } from 'antd';
import HelpTip from './HelpTip';
import { SUBSCRIPTION_COLUMN_HELP } from '../brokerageSubscriptionHelp';
import { MSG_SAVE_SUCCESS, MSG_ERROR } from '../../../../../../../constants';
import { getApiErrorMessage } from '../../../../../../../utils/httpError';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const toFilters = (arr) => [...new Set(arr.filter(Boolean))].sort().map((v) => ({ text: v, value: v }));

const toDisplayFilters = (arr) =>
  [...new Set(arr.map((v) => (v != null && v !== '' ? String(v) : '-')))].sort().map((v) => ({ text: v, value: v }));

const getStatusColumnValue = (record) => {
  const { status } = record;
  if (status === 'Plan Deleted' || status === 'PlanDeleted') return 'Plan Deleted';
  if (record.isRevoked || status === 'Revoked') return 'Revoked';
  if (record.isExpired || status === 'Expired') return 'Expired';
  return status || 'Active';
};

const isRevokedSubscription = (record) => getStatusColumnValue(record) === 'Revoked';

const getActiveDateColumnValue = (record) => record.activeDate || 'Not activated';
const getExpiryDateColumnValue = (record) => record.expiryDate || '-';
const getPromoCodeColumnValue = (record) => record.promoCode || '-';
const getCompanyColumnValue = (record) => record.organisationName || '-';
const getPlanColumnValue = (record) => record.planName || '-';
const getEmailColumnValue = (record) => record.assignedTo || '-';

const SORTERS = {
  company: (a, b) => getCompanyColumnValue(a).localeCompare(getCompanyColumnValue(b)),
  planName: (a, b) => getPlanColumnValue(a).localeCompare(getPlanColumnValue(b)),
  activeDate: (a, b) => {
    const ta = a.activeDate ? new Date(a.activeDate).getTime() : 0;
    const tb = b.activeDate ? new Date(b.activeDate).getTime() : 0;
    return ta - tb;
  },
  expiryDate: (a, b) => {
    const ta = a.exactExpiryDate ? new Date(a.exactExpiryDate).getTime() : 0;
    const tb = b.exactExpiryDate ? new Date(b.exactExpiryDate).getTime() : 0;
    return ta - tb;
  },
  email: (a, b) => getEmailColumnValue(a).localeCompare(getEmailColumnValue(b)),
  totalPlansUsedByUser: (a, b) => (Number(a.totalPlansUsedByUser) || 0) - (Number(b.totalPlansUsedByUser) || 0),
  promoCode: (a, b) => getPromoCodeColumnValue(a).localeCompare(getPromoCodeColumnValue(b)),
};

const applyFilters = (data, filters) => {
  let result = data;

  if (filters.company?.length) {
    result = result.filter((r) => filters.company.includes(getCompanyColumnValue(r)));
  }
  if (filters.planName?.length) {
    result = result.filter((r) => filters.planName.includes(getPlanColumnValue(r)));
  }
  if (filters.activeDate?.length) {
    result = result.filter((r) => filters.activeDate.includes(getActiveDateColumnValue(r)));
  }
  if (filters.expiryDate?.length) {
    result = result.filter((r) => filters.expiryDate.includes(getExpiryDateColumnValue(r)));
  }
  if (filters.email?.length) {
    result = result.filter((r) => filters.email.includes(getEmailColumnValue(r)));
  }
  if (filters.status?.length) {
    result = result.filter((r) => filters.status.includes(getStatusColumnValue(r)));
  }
  if (filters.promoCode?.length) {
    result = result.filter((r) => filters.promoCode.includes(getPromoCodeColumnValue(r)));
  }

  return result;
};

const applySort = (data, key, order) => {
  if (!key || !order) return data;
  const fn = SORTERS[key];
  if (!fn) return data;
  const sorted = [...data].sort(fn);
  return order === 'descend' ? sorted.reverse() : sorted;
};

const applySearch = (data, query) => {
  if (!query) return data;
  const regex = new RegExp(query.replace(/\W|_/g, '[$&]'), 'i');
  return data.filter((row) => {
    const values = [
      row.brokerName,
      getEmailColumnValue(row),
      getCompanyColumnValue(row),
      getPlanColumnValue(row),
      getActiveDateColumnValue(row),
      getExpiryDateColumnValue(row),
      getPromoCodeColumnValue(row),
      getStatusColumnValue(row),
      row.totalPlansUsedByUser,
    ];
    return values.some((value) => value != null && value !== '' && String(value).match(regex));
  });
};

export default ({ subscriptions = [], revoke, renew, reinstate }) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [activeFilters, setActiveFilters] = useState({});
  const [sorterKey, setSorterKey] = useState(null);
  const [sorterOrder, setSorterOrder] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [showExpired, setShowExpired] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [subscriptions.length]);

  const onRevoke = async (record) => {
    const payload = record.subscriptionId ? { subscriptionId: record.subscriptionId } : { subscriptionKey: record.key };
    try {
      await revoke(payload);
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onRenew = async (record) => {
    const subscriptionId = record.subscriptionId ?? record.SubscriptionId;
    if (!subscriptionId) {
      message.error('This subscription cannot be renewed because no subscription id was returned from the API.');
      return;
    }

    try {
      await renew({ subscriptionId });
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const onReinstate = async (record) => {
    const subscriptionId = record.subscriptionId ?? record.SubscriptionId;
    if (!subscriptionId) {
      message.error('This subscription cannot be reinstated because no subscription id was returned from the API.');
      return;
    }

    try {
      await reinstate({ subscriptionId });
      message.success(MSG_SAVE_SUCCESS);
    } catch (err) {
      message.error(getApiErrorMessage(err, MSG_ERROR));
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const nextSorterKey = sorter.columnKey || null;
    const nextSorterOrder = sorter.order || null;
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(activeFilters);
    const sorterChanged = nextSorterKey !== sorterKey || nextSorterOrder !== sorterOrder;

    setActiveFilters(filters);
    setSorterKey(nextSorterKey);
    setSorterOrder(nextSorterOrder);

    if (pagination && pagination.pageSize) setPageSize(Number(pagination.pageSize));
    setPage(filtersChanged || sorterChanged ? 1 : pagination.current || 1);
  };

  const sortOrder = (key) => (sorterKey === key ? sorterOrder : false);
  const filteredValue = (key) => (activeFilters[key]?.length ? activeFilters[key] : null);

  const visibleSubscriptions = useMemo(
    () => (showExpired ? subscriptions : subscriptions.filter((s) => !s.isExpired)),
    [subscriptions, showExpired],
  );

  const companyFilters = useMemo(
    () => toDisplayFilters(visibleSubscriptions.map(getCompanyColumnValue)),
    [visibleSubscriptions],
  );
  const planFilters = useMemo(
    () => toDisplayFilters(visibleSubscriptions.map(getPlanColumnValue)),
    [visibleSubscriptions],
  );
  const emailFilters = useMemo(
    () => toDisplayFilters(visibleSubscriptions.map(getEmailColumnValue)),
    [visibleSubscriptions],
  );
  const activeDateFilters = useMemo(
    () => toDisplayFilters(visibleSubscriptions.map(getActiveDateColumnValue)),
    [visibleSubscriptions],
  );
  const expiryDateFilters = useMemo(
    () => toDisplayFilters(visibleSubscriptions.map(getExpiryDateColumnValue)),
    [visibleSubscriptions],
  );
  const statusFilters = useMemo(
    () => toFilters(visibleSubscriptions.map(getStatusColumnValue)),
    [visibleSubscriptions],
  );
  const promoCodeFilters = useMemo(
    () => toDisplayFilters(visibleSubscriptions.map(getPromoCodeColumnValue)),
    [visibleSubscriptions],
  );

  const getStatus = (record) => {
    const label = getStatusColumnValue(record);
    if (label === 'Plan Deleted') return { label, mod: 'plan-deleted' };
    if (label === 'Revoked') return { label, mod: 'revoked' };
    if (label === 'Expired') return { label, mod: 'expired' };
    return { label, mod: 'active' };
  };

  const columns = [
    {
      key: 'company',
      title: 'Company',
      dataIndex: 'organisationName',
      sorter: true,
      sortOrder: sortOrder('company'),
      filters: companyFilters,
      filteredValue: filteredValue('company'),
      render: (text) => text || '-',
    },
    {
      key: 'planName',
      title: 'Plan Name',
      dataIndex: 'planName',
      sorter: true,
      sortOrder: sortOrder('planName'),
      filters: planFilters,
      filteredValue: filteredValue('planName'),
      render: (text) => text || '-',
    },
    {
      key: 'activeDate',
      title: 'Activate Date',
      dataIndex: 'activeDate',
      sorter: true,
      sortOrder: sortOrder('activeDate'),
      filters: activeDateFilters,
      filteredValue: filteredValue('activeDate'),
      render: (text) => <span>{text || 'Not activated'}</span>,
    },
    {
      key: 'expiryDate',
      title: 'Expiry Date',
      dataIndex: 'expiryDate',
      sorter: true,
      sortOrder: sortOrder('expiryDate'),
      filters: expiryDateFilters,
      filteredValue: filteredValue('expiryDate'),
      render: (text) => text || '-',
    },
    {
      key: 'email',
      title: 'Email',
      dataIndex: 'assignedTo',
      className: 'bsub-email-col',
      width: 220,
      sorter: true,
      sortOrder: sortOrder('email'),
      filters: emailFilters,
      filteredValue: filteredValue('email'),
      render: (text) =>
        text ? (
          <span className="bsub-email-cell" title={text}>
            <a href={`mailto:${text}`}>{text}</a>
          </span>
        ) : (
          '-'
        ),
    },
    {
      key: 'totalPlansUsedByUser',
      title: (
        <span className="bsub-th-label">
          Plans used
          <HelpTip title={SUBSCRIPTION_COLUMN_HELP.plansUsed} />
        </span>
      ),
      dataIndex: 'totalPlansUsedByUser',
      className: 'bsub-plans-used-col',
      width: 128,
      align: 'center',
      sorter: true,
      sortOrder: sortOrder('totalPlansUsedByUser'),
      render: (value) => (value != null && value !== '' ? value : '-'),
    },
    {
      key: 'status',
      title: (
        <span className="bsub-th-label">
          Status
          <HelpTip title={SUBSCRIPTION_COLUMN_HELP.status} />
        </span>
      ),
      filters: statusFilters,
      filteredValue: filteredValue('status'),
      render: (text, record) => {
        const { label, mod } = getStatus(record);
        return <span className={`bsub-status bsub-status--${mod}`}>{label}</span>;
      },
    },
    {
      key: 'promoCode',
      title: (
        <span className="bsub-th-label">
          Promo Code
          <HelpTip title={SUBSCRIPTION_COLUMN_HELP.promoCode} />
        </span>
      ),
      dataIndex: 'promoCode',
      sorter: true,
      sortOrder: sortOrder('promoCode'),
      filters: promoCodeFilters,
      filteredValue: filteredValue('promoCode'),
      render: (_, record) => getPromoCodeColumnValue(record),
    },
    {
      key: 'action',
      title: (
        <span className="bsub-th-label">
          Actions
          <HelpTip title={SUBSCRIPTION_COLUMN_HELP.actions} />
        </span>
      ),
      render: (text, record) => {
        const revoked = isRevokedSubscription(record);

        return (
          <div className="bsub-action-cell">
            {renew && record.isRenewable && !revoked && (
              <Popconfirm title="Renew this subscription?" onConfirm={() => onRenew(record)}>
                <Button size="small" className="bsub-renew-btn">
                  Renew
                </Button>
              </Popconfirm>
            )}
            {revoke && !revoked && (
              <Popconfirm title="Sure to revoke this subscription?" onConfirm={() => onRevoke(record)}>
                <Button size="small" className="bsub-revoke-btn">
                  Revoke
                </Button>
              </Popconfirm>
            )}
            {reinstate && revoked && (
              <Popconfirm title="Reinstate this subscription?" onConfirm={() => onReinstate(record)}>
                <Button size="small" className="bsub-reinstate-btn">
                  Reinstate
                </Button>
              </Popconfirm>
            )}
          </div>
        );
      },
    },
  ];

  const filtered = useMemo(() => {
    const searched = applySearch(visibleSubscriptions, searchText);
    return applySort(applyFilters(searched, activeFilters), sorterKey, sorterOrder);
  }, [visibleSubscriptions, searchText, activeFilters, sorterKey, sorterOrder]);

  const paged = filtered;

  return (
    <div className="bsub-subscriptions-wrap">
      <div className="table-search-container">
        <Checkbox
          checked={showExpired}
          onChange={(e) => {
            const nextShowExpired = e.target.checked;
            setShowExpired(nextShowExpired);
            if (!nextShowExpired) {
              setActiveFilters((prev) => {
                const status = (prev.status || []).filter((value) => value !== 'Expired');
                if (status.length === (prev.status || []).length) return prev;
                return { ...prev, status: status.length ? status : null };
              });
            }
            setPage(1);
          }}
          className="table-toggle"
        >
          Show Expired
        </Checkbox>
        <Input
          placeholder="Search companies, emails, brokers..."
          prefix={<Icon type="search" />}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1);
          }}
          className="table-filter"
          allowClear
        />
      </div>

      <Table
        columns={columns}
        dataSource={paged}
        rowKey={(record) => record.subscriptionId || record.key}
        rowClassName={(record) => (record.isExpired ? 'isExpired' : '')}
        pagination={{
          current: page,
          pageSize,
          total: filtered.length,
          showSizeChanger: true,
          pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
          hideOnSinglePage: false,
          showTotal: (total, range) => `Showing ${range[0]}–${range[1]} of ${total} subscriptions`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1112 }}
        locale={{
          emptyText: subscriptions.length ? 'No results match your search or filters.' : 'No subscriptions',
        }}
      />
    </div>
  );
};

